import type {
  ApiNodeDataExternalApi,
  ProviderIntegrationKind,
  ProviderKind,
  VendorIntegration,
  VendorIntegrationApi,
} from '@onefootprint/request-types/dashboard';

type IntegrationTarget = { kind?: ProviderIntegrationKind; integrationId?: string };

type TargetOption = { value: string; label: string; api: VendorIntegrationApi; provider: ProviderKind };

const SEPARATOR = '|';

const getVendorIntegrationSource = ({ source }: ApiNodeDataExternalApi) =>
  typeof source === 'object' && 'vendorIntegration' in source ? source.vendorIntegration : {};

// A node without an id runs against the tenant's default integration, so the id only enters the
// value when the vendor has several and the choice matters.
const buildTargetOptions = (integrations: VendorIntegration[]): TargetOption[] => {
  const countByProvider = new Map<string, number>();
  for (const { provider } of integrations) {
    countByProvider.set(provider, (countByProvider.get(provider) ?? 0) + 1);
  }
  return integrations.flatMap(integration => {
    const isOneOfSeveral = (countByProvider.get(integration.provider) ?? 0) > 1 && !!integration.id;
    return integration.apis.map(api => ({
      value: isOneOfSeveral ? `${api.kind}${SEPARATOR}${integration.id}` : api.kind,
      label: isOneOfSeveral ? `${api.name} · ${integration.integrationName}` : api.name,
      api,
      provider: integration.provider,
    }));
  });
};

const decodeTarget = (target?: string): IntegrationTarget => {
  if (!target) return {};
  const [kind, integrationId] = target.split(SEPARATOR);
  return { kind: kind as ProviderIntegrationKind, integrationId };
};

// A node saved before its vendor had a second integration carries no id; it runs against the
// oldest one, which the API lists first.
const findTargetOption = (options: TargetOption[], { kind, integrationId }: IntegrationTarget) => {
  if (!kind) return undefined;
  const exact = integrationId ? `${kind}${SEPARATOR}${integrationId}` : kind;
  return options.find(o => o.value === exact) ?? options.find(o => decodeTarget(o.value).kind === kind);
};

// Only an option that names an integration moves the node to another one. Switching to another API
// of the same vendor keeps the node's integration; switching vendor starts from the new one's default.
const resolveIntegrationId = (options: TargetOption[], existing: IntegrationTarget, target?: string) => {
  const { kind, integrationId } = decodeTarget(target);
  if (integrationId) return integrationId;
  const providerOf = (k?: ProviderIntegrationKind) => options.find(o => decodeTarget(o.value).kind === k)?.provider;
  return providerOf(kind) === providerOf(existing.kind) ? existing.integrationId : undefined;
};

export { buildTargetOptions, decodeTarget, findTargetOption, getVendorIntegrationSource, resolveIntegrationId };
